import { showToast } from '../../../engine/toast';
import {
  adminGrant,
  adminPlayerColonies,
  RESOURCE_TYPES,
  type AdminColony,
  type AdminGrantBody,
  type AdminUser,
} from '../admin-api';
import { escapeHtml, INPUT_CLS, BTN_CLS } from '../admin-ui';
import type { PaneCtx } from '../panes/types';
import { errorText } from './fields';

const RESOURCE_LABELS: Record<string, string> = {
  REGOLITH: 'Regolith',
  WATER_ICE: 'Water ice',
  HE3: 'Helium-3',
  COMPOSITE: 'Composite',
  CRIMSON_DUST: 'Crimson dust',
};

function amountField(name: string, label: string): string {
  return `
    <label class="flex items-center gap-2">
      <span class="w-28 shrink-0 text-[11px] text-text-dim">${escapeHtml(label)}</span>
      <input name="${name}" type="number" min="1" step="1" placeholder="0" inputmode="numeric"
        class="${INPUT_CLS}" />
    </label>`;
}

function colonyPicker(colonies: AdminColony[] | null): string {
  if (colonies === null) return '<p class="text-xs text-text-dim">Loading colonies&hellip;</p>';
  if (colonies.length === 0) {
    return `<p class="text-xs text-text-dim">This player has no colony, so resources cannot be granted. Currencies still can.</p>`;
  }
  return `
    <label class="flex flex-col gap-1">
      <span class="text-[10px] text-text-dim">Colony to receive resources</span>
      <select name="colonyId" class="${INPUT_CLS}">
        ${colonies
          .map(
            (c) =>
              `<option value="${escapeHtml(c.id)}">${escapeHtml(c.name)}${c.world ? ` &middot; ${escapeHtml(c.world.name)}` : ''}</option>`,
          )
          .join('')}
      </select>
      ${colonies.length > 1 ? '<span class="text-[10px] text-text-dim">This player owns several colonies — resources go only to the one selected.</span>' : ''}
    </label>`;
}

function bodyHtml(colonies: AdminColony[] | null): string {
  return `
    <form id="grant" data-form="grant" class="flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <h3 class="text-[10px] text-text-dim uppercase tracking-wide">Currencies</h3>
        ${amountField('soft', 'Soft currency')}
        ${amountField('premium', 'Delta-V')}
      </div>

      <div class="flex flex-col gap-2">
        <h3 class="text-[10px] text-text-dim uppercase tracking-wide">Resources</h3>
        <div data-colony-picker>${colonyPicker(colonies)}</div>
        ${RESOURCE_TYPES.map((t) => amountField(`res_${t}`, RESOURCE_LABELS[t] ?? t)).join('')}
        <p class="text-[10px] text-text-dim leading-relaxed">
          A colony only takes what fits under its storage cap; the result reports what actually landed.
        </p>
      </div>

      <label class="flex flex-col gap-1">
        <span class="text-[10px] text-text-dim">Note (recorded in the ledger)</span>
        <input name="reason" type="text" placeholder="Why this grant was made" class="${INPUT_CLS}" />
      </label>

      <div class="flex justify-end gap-2">
        <button type="button" data-action="close" class="min-h-11 px-4 text-sm text-text-dim hover:text-text touch-manipulation">Cancel</button>
        <button type="submit" class="${BTN_CLS}">Grant</button>
      </div>
    </form>`;
}

function describe(result: { soft?: number; premium?: number; resources: Record<string, number> }): string {
  const parts: string[] = [];
  if (result.soft) parts.push(`${result.soft} soft`);
  if (result.premium) parts.push(`${result.premium} Delta-V`);
  for (const [type, amount] of Object.entries(result.resources ?? {})) {
    if (amount) parts.push(`${amount} ${RESOURCE_LABELS[type] ?? type}`);
  }
  return parts.length ? `Granted ${parts.join(', ')}.` : 'Nothing was granted.';
}

/**
 * Grant currencies and resources in a single submit.
 *
 * Core's add-credits takes one currency per call and cannot reach game
 * resources at all, which is why this posts to the game-owned
 * `POST /admin/players/:id/grant`.
 */
export function openAddCredits(ctx: PaneCtx, user: AdminUser): void {
  let colonies: AdminColony[] | null = null;

  const handle = ctx.openModal({
    title: 'Add credits',
    subtitle: user.username ?? user.email,
    size: 'lg',
    body: bodyHtml(null),

    onSubmit(kind, form, h) {
      if (kind !== 'grant') return;
      const data = new FormData(form);
      const num = (name: string): number | undefined => {
        const raw = String(data.get(name) ?? '').trim();
        if (!raw) return undefined;
        const n = Math.floor(Number(raw));
        return Number.isFinite(n) && n > 0 ? n : undefined;
      };

      const resources = RESOURCE_TYPES.map((t) => ({ type: t as string, amount: num(`res_${t}`) }))
        .filter((r): r is { type: string; amount: number } => r.amount !== undefined);

      const body: AdminGrantBody = {
        soft: num('soft'),
        premium: num('premium'),
        reason: String(data.get('reason') ?? '').trim() || undefined,
      };
      if (resources.length > 0) {
        const colonyId = String(data.get('colonyId') ?? '');
        if (!colonyId) {
          h.setError('Pick a colony before granting resources.');
          return;
        }
        body.colonyId = colonyId;
        body.resources = resources;
      }
      if (!body.soft && !body.premium && resources.length === 0) {
        h.setError('Enter at least one amount.');
        return;
      }

      h.setBusy(true);
      void (async () => {
        try {
          const result = await adminGrant(user.id, body);
          // Reports what the server actually applied, not what was typed — a
          // near-full stockpile takes only its remaining headroom.
          showToast(describe(result), 'success');
          h.close();
          await ctx.reload('players');
        } catch (err) {
          h.setError(errorText(err, 'Could not complete that grant.'));
          h.setBusy(false);
        }
      })();
    },
  });

  void (async () => {
    try {
      colonies = await adminPlayerColonies(user.id);
      if (ctx.disposed()) return;
      // Patch ONLY the picker. Replacing the whole body would wipe any
      // amounts already typed while the request was in flight.
      const slot = handle.el.querySelector('[data-colony-picker]');
      if (slot) slot.innerHTML = colonyPicker(colonies);
    } catch {
      const slot = handle.el.querySelector('[data-colony-picker]');
      if (slot) slot.innerHTML = '<p class="text-xs text-crimson">Could not load this player’s colonies.</p>';
    }
  })();
}
